"use client";

import { useEffect } from "react";
import { captureClientError } from "@/lib/monitoring/client-capture";
import { ERROR_CODES } from "@/lib/errors/codes";

/**
 * Route-level React error boundary. Fires when a render or effect throws
 * inside a page (but the app shell still loads). Severity is `error`;
 * unrecoverable shell-level failures escalate to `global-error.tsx`
 * which captures at `critical`.
 *
 * Plan ref: §5 Phase 4.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    captureClientError(error, {
      severity: "error",
      category: "REACT",
      errorCode: ERROR_CODES.REACT_BOUNDARY_FAILED,
      operation: "app/error.tsx",
    });
  }, [error]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-2xl font-bold text-gray-900">
          Something went wrong
        </h1>
        <p className="text-gray-600">
          We hit an unexpected error rendering this page. Try again, or head
          back to the dashboard.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => reset()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Try again
          </button>
          <button
            onClick={() => (window.location.href = "/")}
            className="px-4 py-2 bg-gray-200 text-gray-900 rounded-md hover:bg-gray-300 transition-colors"
          >
            Go home
          </button>
        </div>
      </div>
    </div>
  );
}
